import { PASS_PRINT_GEOMETRY } from '@sentra/contracts';
import { Ruler } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import type { PrintSurface } from '@/features/scan/print';

/**
 * Proving the printer is not shrinking the page, with a ruler.
 *
 * **The commonest way a printed pass stops scanning is not a bad symbol.** It is a printer set to
 * "fit to page" or "shrink oversized", which takes the measured 31.9 mm square down by five or ten
 * percent. The symbol still looks perfect, it still decodes on a screen, and it reads slowly or not
 * at all off paper under a scanner at a gate — which is diagnosed as a broken scanner, or as a
 * broken product, because nothing anywhere says the page was resized.
 *
 * So this prints one square at exactly the geometry the pass sheet uses, and asks somebody to
 * measure it. A ruler is the only instrument that can answer the question, and this is a check an
 * operator runs once when a printer is installed rather than a gate anything can automate.
 *
 * **The square is the FULL geometry, quiet zone included**, because that is the extent the sheet
 * lays out and therefore the thing a scale factor acts on. Measuring the symbol alone would mean
 * measuring to the edge of a pattern that has no edge anybody can see.
 */

/** The one number this sheet is about, taken from the geometry both renderers already share. */
const SQUARE_MM = PASS_PRINT_GEOMETRY.totalMm;

/**
 * The calibration page, as one self-contained document.
 *
 * The same shape as the pass sheet for the same reasons: no script, no external stylesheet and no
 * web font, so it renders offline at the size it was measured at and satisfies a
 * `default-src 'self'` policy with no `'unsafe-inline'` in `script-src`.
 */
export function renderCalibrationSheet(text: {
  readonly title: string;
  readonly body: string;
  readonly square: string;
}): string {
  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>${escapeHtml(text.title)}</title>
<style>
@page { size: 210mm 297mm; margin: 0; }
html, body { margin: 0; padding: 0; }
body {
  width: 210mm; height: 297mm;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10mm;
  font-family: system-ui, sans-serif;
  color: #000; background: #fff;
}
.calibration-square {
  width: ${String(SQUARE_MM)}mm; height: ${String(SQUARE_MM)}mm;
  border: 0.4mm solid #000;
}
.calibration-note { width: 120mm; font-size: 4mm; line-height: 1.5; text-align: center; }
</style>
</head>
<body>
<div class="calibration-square" role="img" aria-label="${escapeHtml(text.square)}"></div>
<p class="calibration-note">${escapeHtml(text.body)}</p>
</body>
</html>
`;
}

/** The five characters that would otherwise let a value close a tag or an attribute. */
const HTML_ESCAPES: Readonly<Record<string, string>> = Object.freeze({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
});

/**
 * Escapes a value for HTML.
 *
 * Every value on this sheet comes from this application's own catalogue rather than from anybody's
 * input, so nothing here is untrusted — but a document assembled by string concatenation that does
 * not escape is a document somebody will later interpolate a name into.
 */
function escapeHtml(value: string): string {
  return value.replaceAll(/[&<>"']/gu, (character) => HTML_ESCAPES[character] ?? character);
}

export function PrinterCalibration({ surface }: Readonly<{ surface: PrintSurface }>): ReactNode {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const millimetres = SQUARE_MM.toFixed(1);

  return (
    <div className="grid gap-2">
      <h3 className="text-sm font-semibold">{t('scan.delivery.calibrateTitle')}</h3>
      <p className="text-sm text-muted-foreground">
        {t('scan.delivery.calibrateBody', { millimetres })}
      </p>
      <Button
        type="button"
        variant="ghost"
        onClick={() => {
          setFailed(false);
          void surface
            .print(
              renderCalibrationSheet({
                title: t('scan.delivery.calibrateTitle'),
                body: t('scan.delivery.calibrateBody', { millimetres }),
                square: t('scan.delivery.calibrateSquare', { millimetres }),
              }),
            )
            .catch(() => {
              setFailed(true);
            });
        }}
      >
        <Ruler aria-hidden />
        {t('scan.delivery.calibratePrint')}
      </Button>
      {failed && (
        <p role="alert" className="text-sm text-danger">
          {t('scan.delivery.printFailed')}
        </p>
      )}
    </div>
  );
}
